import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import InputMask from 'react-input-mask';
import { Auth } from 'aws-amplify';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';
import { getAuthState, getOnboardingState } from '../../store/selectors';
import {
  getUserDashboardInfo,
  getSchools,
  updateAdvisorDashboardUserProfile,
  getEligibleUnassignedAdvisorsForUser,
  assignAdvisorsToUser,
  unassignAdvisorFromUser,
} from '../../store/actions';
import ErrorMessage from '../ErrorMessage';
import EditButton from './user-profile-components/EditButton';
import SelectDropdown from './user-profile-components/SelectDropdown';
import UserInformation from './user-profile-components/UserInformation';
import countryTerritories from '../../util/countryTerritories.json';
import countriesList from '../../util/countriesList.json';
import { userProfileCopy } from '../../copy/advisor-dashboard/user-profile.json';
import HighSchoolInputs from '../auth/HighSchoolInputs';
import ChangesSaved from '../utility/ChangesSaved';
import validate from '../utility/CheckError';
import { submitButton_GTM } from '../../gtm/datalayers/utilDataLayers.json';
import { fireGTM } from '../../gtm/gtmUtil';
import { getSSOProvider } from '../utility/getSSOProvider';

/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/jsx-indent */

function UserProfileForm(props) {
  const useStyles = createUseStyles({
    saveChangesButton: {
      color: 'white',
      borderColor: props.userProfile?.tenant.primary,
      backgroundColor: props.userProfile?.tenant.primary,
      padding: ' 0 34px',

      '&:hover': {
        border: `2px solid ${props.userProfile?.tenant.primary}`,
        color: `${props.userProfile?.tenant.primary}`,
      },
      '&:disabled': {
        color: 'white',
        borderColor: props.userProfile?.tenant.primary,
        backgroundColor: props.userProfile?.tenant.primary,
        padding: ' 0 34px',
        pointerEvents: 'none',
      },
    },
  });

  const classes = useStyles(props);

  const errorList = {
    dateOfBirth: false,
    customSchool: false,
    firstName: false,
    lastName: false,
    highSchool: false,
  };

  const [editFirstName, setEditFirstName] = useState(false);
  const [editLastName, setEditLastName] = useState(false);
  const [editDOB, setEditDOB] = useState(false);
  const [editCountry, setEditCountry] = useState(false);
  const [editState, setEditState] = useState(false);
  const [editEducationLevel, setEditEducationLevel] = useState(false);
  const [editHighSchool, setEditHighSchool] = useState(false);
  const [editGraduationYear, setEditGraduationYear] = useState(false);
  const [editAdvisor, setEditAdvisor] = useState(false);
  const [inputs, setInputs] = useState(props.userProfile);
  const [errors, setErrors] = useState(errorList);
  const [errorsPresent, setErrorsPresent] = useState(false);
  const [listOfTerritories, setListOfTerritories] = useState({});
  const [listOfCountries, setListOfCountries] = useState([]);
  const [advisorValues, setAdvisorValues] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [advisorsChecked, setAdvisorsChecked] = useState(false);
  const [updateProfileSuccess, setUpdateProfileSuccess] = useState(false);
  const [updateEmailSuccess, setUpdateEmailSuccess] = useState(false);
  const [requestDenied, setRequestDenied] = useState(false);

  const currentYear = new Date().getFullYear();
  const yearChoices = [
    { value: currentYear, label: currentYear },
    { value: currentYear + 1, label: currentYear + 1 },
    { value: currentYear + 2, label: currentYear + 2 },
    { value: currentYear + 3, label: currentYear + 3 },
    { value: currentYear + 4, label: currentYear + 4 },
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      height: '100%',
      borderRadius: '7px',
    }),
    option: (provided) => ({
      ...provided,
      color: props.userProfile?.tenant.primary,
    }),
    input: (provided) => ({
      ...provided,
      color: props.userProfile?.tenant.primary,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'black',
      paddingLeft: '12px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'black',
      overflow: 'visible',
      paddingLeft: '12px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      paddingRight: '6px',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: props,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
  };

  const updateHighSchool = () => {
    if (inputs.customSchool && inputs.highSchool === 'Other') {
      return inputs.customSchool;
    }
    if (inputs.educationLevel !== 'Currently in high school') {
      return null;
    }
    return inputs.highSchool;
  };

  const handleForgotPasswordClick = async () => {
    try {
      fireGTM(submitButton_GTM, 'submit', 'forgot your password?');
      await Auth.forgotPassword(inputs.email);
      setUpdateEmailSuccess(true);
      setTimeout(() => {
        setUpdateEmailSuccess(false);
      }, 5000);
    } catch (error) {
      console.error(error);
      setRequestDenied(true);
      setTimeout(() => {
        setRequestDenied(false);
      }, 5000);
    }
  };

  const getAdvisors = async () => {
    const advisorList = await props.getEligibleUnassignedAdvisorsForUser(
      inputs.tenantId,
      inputs.id,
      inputs.highSchool
    );
    const advisorOptions = advisorList.map((advisor) => {
      return {
        ...advisor,
        value: advisor.advisorName,
        label: advisor.advisorName,
      };
    });
    setAdvisors(advisorOptions);
  };

  const handleAssignAdviors = async (checkedAdvisors) => {
    if (checkedAdvisors) {
      const advisorIds = checkedAdvisors.map((x) => x.advisorId);
      await props.assignAdvisorsToUser(
        props.userProfile?.id,
        props.userProfile?.tenantId,
        advisorIds
      );
      await getAdvisors();
    }
  };

  const removeAdvisor = async (advisor) => {
    await props.unassignAdvisorFromUser(
      props.userProfile?.id,
      props.userProfile?.tenantId,
      advisor.advisorId
    );
    await props.getUserDashboardInfo(
      props.userProfile?.id,
      props.userProfile?.tenantId
    );
    await getAdvisors();
  };

  const updateUserProfile = async () => {
    if (
      props.userProfile?.highSchool !== inputs.highSchool &&
      props.userProfile?.assignedAdvisors
    ) {
      return props.userProfile?.assignedAdvisors.map((advisor) => {
        removeAdvisor(advisor);
      });
    }
    await handleAssignAdviors(advisorValues);
    const updatedUser = {
      id: props.userProfile?.id,
      firstName: inputs.firstName,
      lastName: inputs.lastName,
      dateOfBirth: inputs.dateOfBirth,
      country: inputs.country,
      state: inputs.state,
      educationLevel: inputs.educationLevel,
      highSchool: updateHighSchool(),
      graduationYear: inputs.graduationYear,
      tenantId: props.userProfile?.tenantId,
    };
    await props
      .updateAdvisorDashboardUserProfile(updatedUser, props.auth.jwtToken)
      .then((res) => {
        if (res === 200) {
          setUpdateProfileSuccess(true);
        }
      });
  };

  const saveChanges = async () => {
    await updateUserProfile();
    setEditFirstName(false);
    setEditLastName(false);
    setEditDOB(false);
    setEditCountry(false);
    setEditState(false);
    setEditEducationLevel(false);
    setEditHighSchool(false);
    setEditGraduationYear(false);
    setEditAdvisor(false);
  };

  const handleErrors = async (
    event,
    inputValue,
    initialState,
    checkEvent = true
  ) => {
    if (checkEvent) event.persist();

    const errorsCopy = cloneDeep(errors);
    const stateCopy = cloneDeep(inputs);

    const error = await validate(
      event.target.id,
      inputValue,
      errorsCopy,
      stateCopy,
      initialState
    );

    setErrors(error);
  };

  const blurDateOfBirth = (event, dob) => {
    const regex = /(\d\d)(\d\d)(\d\d\d\d)/gi;
    const newDob = dob.replace(regex, '$3-$1-$2');

    setInputs((state) => ({ ...state, dateOfBirth: newDob }), []);

    handleErrors(event, newDob);
  };

  const onChangeHandler = useCallback(({ target: { name, value } }) => {
    setUpdateProfileSuccess(false);
    setInputs((state) => ({ ...state, [name]: value }), []);
  });

  const handleSelect = async (name, option) => {
    setUpdateProfileSuccess(false);
    if (inputs.country === 'United States' && inputs.state === null) {
      setEditState(true);
    }
    if (
      option.value !== 'Currently in high school' &&
      name === 'educationLevel'
    ) {
      setInputs((state) => ({
        ...state,
        [name]: option.value,
        highSchool: null,
        graduationYear: null,
      }));
      setEditHighSchool(false);
      setEditGraduationYear(false);
    }
    if (
      name === 'state' &&
      inputs.country === 'United States' &&
      inputs.educationLevel === 'Currently in high school'
    ) {
      setInputs((state) => ({
        ...state,
        [name]: option.value,
        highSchool: null,
      }));
      setErrors((state) => ({
        ...state,
        highSchool: true,
      }));
    }
    if (name === 'highSchool') {
      setInputs((state) => ({ ...state, [name]: option.value }));
      setErrors((state) => ({
        ...state,
        highSchool: false,
      }));
      setAdvisorValues([]);
      setEditAdvisor(false);
    } else if (option.value !== 'United States' && name === 'country') {
      setInputs((state) => ({
        ...state,
        [name]: option.value,
        state: null,
        highSchool: null,
      }));
    } else {
      setInputs((state) => ({ ...state, [name]: option.value }), []);
    }
  };

  const handleCancel = (cancelInput) => {
    setInputs((state) => ({
      ...state,
      [cancelInput]: props.userProfile?.[cancelInput],
    }));
    if (
      cancelInput === 'assignedAdvisors' ||
      ((cancelInput === 'customSchool' || cancelInput === 'highSchool') &&
        editAdvisor)
    ) {
      setAdvisorValues(
        props.userProfile?.[cancelInput] &&
          props.userProfile?.highSchool === inputs.highSchool
          ? props.userProfile?.[cancelInput]
          : []
      );
      setEditAdvisor(false);
    }
    if (Object.prototype.hasOwnProperty.call(errors, cancelInput)) {
      setErrors((state) => ({
        ...state,
        [cancelInput]: false,
      }));
    }
    if (cancelInput === 'country') {
      setInputs((state) => ({
        ...state,
        state: props.userProfile?.state,
        highSchool: props.userProfile?.highSchool,
      }));
    }
    if (cancelInput === 'state') {
      setInputs((state) => ({
        ...state,
        highSchool: props.userProfile?.highSchool,
      }));
    }
    if (
      cancelInput === 'state' &&
      inputs.country === 'United States' &&
      inputs.educationLevel === 'Currently in high school'
    ) {
      setErrors((state) => ({
        ...state,
        highSchool: false,
      }));
    }
    if (cancelInput === 'educationLevel') {
      setInputs((state) => ({
        ...state,
        highSchool: props.userProfile?.highSchool,
        graduationYear: props.userProfile?.graduationYear,
      }));
    }
    if (cancelInput === 'customSchool') {
      setInputs((state) => ({
        ...state,
        customSchool: '',
      }));
    }
    if (cancelInput === 'highSchool') {
      setErrors((state) => ({
        ...state,
        customSchool: false,
      }));
    }
  };

  const isButtonDisabled = () => {
    if (inputs.country === 'United States' && inputs.state === null) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.country === 'United States' &&
      (inputs.highSchool === null || inputs.graduationYear === null) &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.country !== 'United States' &&
      inputs.graduationYear === null &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (errorsPresent) {
      return true;
    }
    if (inputs.highSchool === 'Other' && !inputs.customSchool) {
      return true;
    }
    if (
      props.userProfile?.state !== inputs.state &&
      props.userProfile?.highSchool === inputs.highSchool &&
      inputs.educationLevel === 'Currently in high school' &&
      inputs.country === 'United States' &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      editFirstName ||
      editLastName ||
      editDOB ||
      editCountry ||
      editState ||
      editEducationLevel ||
      editHighSchool ||
      editGraduationYear ||
      editAdvisor
    ) {
      return false;
    }
    return true;
  };

  const getEcapStatus = () => {
    if (!props.userProfile?.tenant.features.includes('highschool')) {
      return 'Inactive Feature';
    }
    if (
      (props.userProfile?.startedEcap === null &&
        props.userProfile?.completedEcap === null) ||
      props.userProfile?.educationLevel !== 'Currently in high school'
    ) {
      return 'N/A';
    }
    if (props.userProfile?.startedEcap && props.userProfile?.completedEcap) {
      return 'Completed';
    }
    if (props.userProfile?.startedEcap) {
      return 'In Progress';
    }
    return 'Not Started';
  };

  const showValidLocationData = () => {
    let countriesToInclude = [];

    Object.keys(countriesList).map((key) => {
      if (countriesList[key].excluded === 'False') {
        return countriesToInclude.push(key);
      }

      return key;
    });
    countriesToInclude.sort();
    countriesToInclude = countriesToInclude.map((country) => {
      return { value: country, label: country };
    });

    countriesToInclude.unshift({
      value: userProfileCopy.location.usaText,
      label: userProfileCopy.location.usaText,
    });

    countriesToInclude.push({
      value: userProfileCopy.location.otherCountryText,
      label: userProfileCopy.location.otherCountryText,
    });

    setListOfCountries(countriesToInclude);
  };

  const getGradYearInfo = () => {
    if (
      props.userProfile?.graduationYear === inputs.graduationYear &&
      props.userProfile?.educationLevel === 'Currently in high school' &&
      (props.userProfile?.tenant.features.includes('highschool') ||
        props.userProfile?.tenant.name === 'Baobab')
    ) {
      return props.userProfile?.graduationYear;
    }
    if (
      !props.userProfile?.tenant.features.includes('highschool') &&
      props.userProfile?.tenant.name !== 'Baobab'
    ) {
      return 'Inactive Feature';
    }
    return 'N/A';
  };

  const getHighSchoolInfo = () => {
    if (
      (props.userProfile?.highSchool === inputs.highSchool &&
        props.userProfile?.tenant.features.includes('highschool') &&
        props.userProfile?.highSchool !== null) ||
      (props.userProfile?.highSchool === inputs.customSchool &&
        props.userProfile?.highSchool !== null &&
        props.userProfile?.tenant.features.includes('highschool'))
    ) {
      return props.userProfile?.highSchool;
    }
    if (!props.userProfile?.tenant.features.includes('highschool')) {
      return 'Inactive Feature';
    }
    return 'N/A';
  };

  const getEditHighSchool = () => {
    if (
      editHighSchool &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.highSchool === null &&
      inputs.country === 'United States' &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.highSchool !== props.userProfile?.highSchool &&
      inputs.country === 'United States' &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.country === 'United States' &&
      inputs.state !== props.userProfile?.state &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    return false;
  };

  const displayEditHighSchoolButton = () => {
    if (
      editHighSchool &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.highSchool === null &&
      inputs.country === 'United States' &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.highSchool !== props.userProfile?.highSchool &&
      inputs.country === 'United States' &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    if (
      inputs.educationLevel === 'Currently in high school' &&
      inputs.country === 'United States' &&
      inputs.state !== props.userProfile?.state &&
      props.userProfile?.tenant.features.includes('highschool')
    ) {
      return true;
    }
    return false;
  };

  const getTerritories = () => {
    const territoryList = Object.values(countryTerritories);
    territoryList.sort();
    const territoryOptions = territoryList.map((territory) => {
      return { value: territory, label: territory };
    });
    setListOfTerritories(territoryOptions);
  };

  const checkErrors = () => {
    let pageHasErrors;

    if (Object.values(errors).indexOf(true) > -1) {
      pageHasErrors = true;
    } else {
      pageHasErrors = false;
    }

    setErrorsPresent(pageHasErrors);
  };

  useEffect(() => {
    props.getSchools(inputs.state);
    getTerritories();
    showValidLocationData();
    checkErrors();
    isButtonDisabled();
    getAdvisors();
  }, [inputs, errors]);

  const Menu = (menuProps) => {
    const optionSelectedLength = menuProps.getValue().length || 0;
    const menuMaxLimit = props.userProfile?.assignedAdvisors
      ? 3 - props.userProfile?.assignedAdvisors.length
      : 3;
    return (
      // eslint-disable-next-line
      <components.Menu {...menuProps}>
        {optionSelectedLength < menuMaxLimit ? (
          menuProps.children
        ) : (
          <div style={{ margin: 15 }}>Max limit achieved</div>
        )}
      </components.Menu>
    );
  };

  const isValidNewOption = (inputValue, selectValue) => {
    const maxLimit = props.userProfile?.assignedAdvisors
      ? 3 - props.userProfile?.assignedAdvisors.length
      : 3;
    return inputValue.length > 0 && selectValue.length < maxLimit;
  };

  const onChangeAdvisor = (opt) => {
    setUpdateProfileSuccess(false);
    setAdvisorValues(opt);
  };

  return (
    <>
      <div className="grid-container">
        {/* First and Last Name */}

        <UserInformation
          label={userProfileCopy.formInputs.input1}
          edit={editFirstName}
          handleEdit={setEditFirstName}
          onChange={onChangeHandler}
          handleCancel={handleCancel}
          input={props.userProfile?.firstName}
          propValue="firstName"
          primaryColor={props.userProfile?.tenant?.primary}
          onBlur={(event) => {
            handleErrors(event, inputs.firstName, props.userProfile?.firstName);
          }}
          isError={errors.firstName}
          editable={props.userProfile?.editable}
        />

        <UserInformation
          label={userProfileCopy.formInputs.input2}
          edit={editLastName}
          handleEdit={setEditLastName}
          onChange={onChangeHandler}
          handleCancel={handleCancel}
          input={props.userProfile?.lastName}
          propValue="lastName"
          primaryColor={props.userProfile?.tenant?.primary}
          onBlur={(event) => {
            handleErrors(event, inputs.lastName, props.userProfile?.lastName);
          }}
          isError={errors.lastName}
          editable={props.userProfile?.editable}
        />

        {/* Email and DOB */}

        <div className="grid-item">
          <span>{userProfileCopy.formInputs.input3}</span>
          <span className="user-grid-item">{inputs.email}</span>
        </div>

        <div className="grid-item">
          <span className="form-input">
            {userProfileCopy.formInputs.input4}
            {editDOB ? (
              <EditButton
                primaryColor={props.userProfile?.tenant?.primary}
                onChange={() => {
                  setEditDOB(false);
                  handleCancel('dateOfBirth');
                }}
              >
                {userProfileCopy.buttons.button4}
              </EditButton>
            ) : (
              props.userProfile?.editable && (
                <EditButton
                  primaryColor={props.userProfile?.tenant?.primary}
                  onChange={() => setEditDOB(true)}
                >
                  {userProfileCopy.buttons.button2}
                </EditButton>
              )
            )}
          </span>
          {editDOB ? (
            <div className="form-label-error-input">
              <div className="label-and-error">
                <label
                  className="label-registration registration-form-column"
                  htmlFor="dob"
                  style={{ color: props.userProfile?.tenant?.primary }}
                >
                  {userProfileCopy.input4}
                </label>
                <span
                  id="dob-error"
                  style={{ display: errors.dateOfBirth ? '' : 'none' }}
                >
                  <ErrorMessage error={userProfileCopy.errors.error1} />
                </span>
              </div>
              <InputMask
                placeholder={moment(props.userProfile?.dateOfBirth).format(
                  'MM/DD/YYYY'
                )}
                id="dateOfBirth"
                className={
                  errors.dateOfBirth
                    ? 'edit-profile-input error'
                    : 'edit-profile-input'
                }
                mask="99/99/9999"
                maskPlaceholder="mm/dd/yyyy"
                key="dateOfBirth"
                name="dateOfBirth"
                onChange={onChangeHandler}
                onBlur={(event) => {
                  blurDateOfBirth(event, inputs.dateOfBirth);
                }}
              />
            </div>
          ) : (
            <span className="user-grid-item">
              {moment(props.userProfile?.dateOfBirth).format('MM/DD/YYYY')}
            </span>
          )}
        </div>

        {/* Password */}
        <div className="grid-item">
          <span className="form-input">
            {userProfileCopy.formInputs.input5}
            <button
              className="link-button edit"
              type="button"
              onClick={() => handleForgotPasswordClick()}
              onKeyPress={(e) => {
                if (['Enter'].includes(e.key)) handleForgotPasswordClick();
              }}
              alt="edit"
              style={{ color: props.userProfile?.tenant.primary }}
              disabled={getSSOProvider(
                props.userProfile?.cognitoUsername,
                props.userProfile?.tenantName
              )}
            >
              {userProfileCopy.buttons.button3}
            </button>
          </span>
          {getSSOProvider(
            props.userProfile?.cognitoUsername,
            props.userProfile?.tenantName
          ) ? (
            <span className="user-grid-item">
              {userProfileCopy.SSOText}
              {getSSOProvider(
                props.userProfile?.cognitoUsername,
                props.userProfile?.tenantName
              )}
              .
            </span>
          ) : (
            <div className="user-grid-item">
              <span>*******</span>
              <ChangesSaved
                message={
                  updateEmailSuccess ? 'Email Request Sent' : 'Request Denied'
                }
                stateValToDetermineShowing={requestDenied || updateEmailSuccess}
                successStatus={updateEmailSuccess}
                typeOfMessage="email"
              />
            </div>
          )}
        </div>

        {/* Country and State */}

        <div className="grid-item">
          <span className="form-input">
            {userProfileCopy.formInputs.input6}
            {editCountry ? (
              <EditButton
                primaryColor={props.userProfile?.tenant.primary}
                onChange={() => {
                  setEditCountry(false);
                  handleCancel('country');
                }}
              >
                {userProfileCopy.buttons.button4}
              </EditButton>
            ) : (
              props.userProfile?.editable && (
                <EditButton
                  primaryColor={props.userProfile?.tenant.primary}
                  onChange={() => setEditCountry(true)}
                >
                  {userProfileCopy.buttons.button2}
                </EditButton>
              )
            )}
          </span>
          {editCountry ? (
            <div className="form-label-error-input padding-right-desktop">
              <SelectDropdown
                id="country"
                callbackFunc={handleSelect.bind(null, 'country')}
                list={listOfCountries}
                placeholder1={props.userProfile?.country}
                placeholder2="Country"
                primaryColor={props.userProfile?.tenant.primary}
              />
            </div>
          ) : (
            <span className="user-grid-item">
              <span className="user-grid-item">
                {props.userProfile?.country}
              </span>
            </span>
          )}
        </div>
        <div className="grid-item">
          <span className="form-input">
            {userProfileCopy.formInputs.input7}
            {(editState && inputs.country === 'United States') ||
            (inputs.country === 'United States' && inputs.state === null) ? (
              <EditButton
                primaryColor={props.userProfile?.tenant.primary}
                onChange={() => {
                  setEditState(false);
                  handleCancel('state');
                }}
                isDisabled={inputs.country !== props.userProfile?.country}
              >
                {userProfileCopy.buttons.button4}
              </EditButton>
            ) : (
              props.userProfile?.editable && (
                <EditButton
                  primaryColor={props.userProfile?.tenant.primary}
                  onChange={() => setEditState(true)}
                  isDisabled={inputs.country !== 'United States'}
                >
                  {userProfileCopy.buttons.button2}
                </EditButton>
              )
            )}
          </span>
          {(editState && inputs.country === 'United States') ||
          (inputs.country === 'United States' && inputs.state === null) ? (
            <div className="form-label-error-input padding-right-desktop">
              <SelectDropdown
                id="state"
                callbackFunc={handleSelect.bind(null, 'state')}
                list={listOfTerritories}
                placeholder1={inputs.state}
                placeholder2="State"
                primaryColor={props.userProfile?.tenant.primary}
              />
            </div>
          ) : (
            <span className="user-grid-item">
              {inputs.country === props.userProfile?.country &&
              props.userProfile?.country === 'United States'
                ? props.userProfile?.state
                : 'N/A'}
            </span>
          )}
        </div>

        {/* Education Level and High School Name */}

        <div className="grid-item">
          <span className="form-input">
            {userProfileCopy.formInputs.input8}
            {editEducationLevel ? (
              <EditButton
                primaryColor={props.userProfile?.tenant.primary}
                onChange={() => {
                  setEditEducationLevel(false);
                  handleCancel('educationLevel');
                }}
              >
                {userProfileCopy.buttons.button4}
              </EditButton>
            ) : (
              props.userProfile?.editable && (
                <EditButton
                  primaryColor={props.userProfile?.tenant.primary}
                  onChange={() => setEditEducationLevel(true)}
                >
                  {userProfileCopy.buttons.button2}
                </EditButton>
              )
            )}
          </span>
          {editEducationLevel ? (
            <div className="form-label-error-input padding-right-desktop">
              <SelectDropdown
                id="educationLevel"
                callbackFunc={handleSelect.bind(null, 'educationLevel')}
                list={Object.values(userProfileCopy.educationLevels)}
                placeholder1={inputs.educationLevel}
                placeholder2="Education"
                primaryColor={props.userProfile?.tenant.primary}
              />
            </div>
          ) : (
            <span className="user-grid-item">
              {props.userProfile?.educationLevel}
            </span>
          )}
        </div>
        <div className="grid-item">
          <span className="form-input" data-testid="high-school">
            {userProfileCopy.formInputs.input9}
            {displayEditHighSchoolButton() ? (
              <EditButton
                primaryColor={props.userProfile?.tenant.primary}
                onChange={() => {
                  setEditHighSchool(false);
                  handleCancel('highSchool');
                }}
                isDisabled={
                  (inputs.educationLevel === 'Currently in high school' &&
                    props.userProfile?.educationLevel !==
                      inputs.educationLevel) ||
                  (inputs.educationLevel === 'Currently in high school' &&
                    inputs.state !== props.userProfile?.state &&
                    inputs.country === 'United States')
                }
              >
                {userProfileCopy.buttons.button4}
              </EditButton>
            ) : (
              props.userProfile?.editable && (
                <EditButton
                  primaryColor={props.userProfile?.tenant.primary}
                  onChange={() => {
                    setEditHighSchool(true);
                    handleCancel('customSchool');
                  }}
                  isDisabled={
                    inputs.educationLevel !== 'Currently in high school' ||
                    inputs.country !== 'United States' ||
                    !props.userProfile?.tenant.features.includes('highschool')
                  }
                >
                  {userProfileCopy.buttons.button2}
                </EditButton>
              )
            )}
          </span>
          {getEditHighSchool() ? (
            <>
              <span
                id="highSchool-error"
                style={{ display: errors.highSchool ? '' : 'none' }}
              >
                <ErrorMessage error={userProfileCopy.errors.error2} />
              </span>
              <div className="form-label-error-input padding-right-desktop">
                <HighSchoolInputs
                  onboarding={props.onboarding}
                  errors={errors}
                  highSchool={inputs.highSchool}
                  customSchool={inputs.customSchool}
                  graduationYear={inputs.graduationYear}
                  handleErrors={handleErrors}
                  primary={props.userProfile?.tenant.primary}
                  handleHighSchoolSelected={handleSelect.bind(
                    null,
                    'highSchool'
                  )}
                  onInputChange={onChangeHandler}
                  userEducationLevel={inputs.educationLevel}
                  userCountry={inputs.country}
                  advisorDashboard
                />
              </div>
            </>
          ) : (
            <span className="user-grid-item">{getHighSchoolInfo()}</span>
          )}
        </div>
        <div className="grid-item">
          <span>{userProfileCopy.formInputs.input10}</span>
          <span className="user-grid-item">{getEcapStatus()}</span>
        </div>
        <div className="grid-item">
          <span className="form-input">
            {userProfileCopy.formInputs.input11}
            {(editGraduationYear &&
              props.userProfile?.tenant.features.includes('highschool')) ||
            (inputs.educationLevel === 'Currently in high school' &&
              inputs.graduationYear === null &&
              props.userProfile?.tenant.features.includes('highschool')) ||
            (inputs.educationLevel === 'Currently in high school' &&
              inputs.graduationYear !== props.userProfile?.graduationYear &&
              props.userProfile?.tenant.features.includes('highschool')) ? (
              <EditButton
                primaryColor={props.userProfile?.tenant.primary}
                onChange={() => {
                  setEditGraduationYear(false);
                  handleCancel('graduationYear');
                }}
                isDisabled={
                  inputs.educationLevel === 'Currently in high school' &&
                  props.userProfile?.educationLevel !== inputs.educationLevel
                }
              >
                {userProfileCopy.buttons.button4}
              </EditButton>
            ) : (
              props.userProfile?.editable && (
                <EditButton
                  primaryColor={props.userProfile?.tenant.primary}
                  onChange={() => setEditGraduationYear(true)}
                  isDisabled={
                    inputs.educationLevel !== 'Currently in high school' ||
                    !props.userProfile?.tenant.features.includes('highschool')
                  }
                >
                  {userProfileCopy.buttons.button2}
                </EditButton>
              )
            )}
          </span>
          {(editGraduationYear &&
            props.userProfile?.tenant.features.includes('highschool')) ||
          (inputs.educationLevel === 'Currently in high school' &&
            inputs.graduationYear === null &&
            props.userProfile?.tenant.features.includes('highschool')) ||
          (inputs.educationLevel === 'Currently in high school' &&
            inputs.graduationYear !== props.userProfile?.graduationYear &&
            props.userProfile?.tenant.features.includes('highschool')) ? (
            <div className="form-label-error-input padding-right-desktop">
              <SelectDropdown
                id="graduationYear"
                callbackFunc={handleSelect.bind(null, 'graduationYear')}
                list={yearChoices}
                placeholder1={props.userProfile?.graduationYear}
                placeholder2="Graduation Year"
                primaryColor={props.userProfile?.tenant.primary}
              />
            </div>
          ) : (
            <span className="user-grid-item">{getGradYearInfo()}</span>
          )}
        </div>
        <div className="grid-item">
          <span className="form-input">
            {userProfileCopy.formInputs.input12}
            {editAdvisor ? (
              <EditButton
                primaryColor={props.userProfile?.tenant.primary}
                onChange={() => {
                  setEditAdvisor(false);
                  handleCancel('assignedAdvisors');
                }}
              >
                {userProfileCopy.buttons.button4}
              </EditButton>
            ) : (
              props.userProfile?.editable && (
                <EditButton
                  primaryColor={props.userProfile?.tenant.primary}
                  onChange={() => setEditAdvisor(true)}
                >
                  {userProfileCopy.buttons.button2}
                </EditButton>
              )
            )}
          </span>
          {editAdvisor ? (
            <div className="form-label-error-input padding-right-desktop advisor-select">
              <CreatableSelect
                isMulti
                id="advisor"
                components={{ Menu }}
                onChange={onChangeAdvisor}
                isValidNewOption={isValidNewOption}
                styles={customStyles}
                className="edit-profile-select"
                options={advisors}
              />
            </div>
          ) : (
            <div className="user-grid-item__advisors">
              {props.userProfile?.assignedAdvisors ? (
                <ul>
                  {props.userProfile?.assignedAdvisors.map((advisor) => {
                    return (
                      <li key={advisor.advisorName}>
                        {advisor.advisorName}
                        <button
                          className="remove-button"
                          type="button"
                          onClick={() => {
                            removeAdvisor(advisor);
                          }}
                        >
                          x
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <span className="user-grid-item">None Selected</span>
              )}
            </div>
          )}
        </div>
        <div className="grid-item">
          <span>{userProfileCopy.formInputs.input13}</span>
          {inputs.tenantsArray?.map((tenant) => {
            return (
              <span className="user-grid-item" key={tenant}>
                {tenant}
              </span>
            );
          })}
        </div>
        <div className="grid-item" />
        <div className="grid-item" />
        <div className="grid-item" />
      </div>
      {props.userProfile?.editable && (
        <div className="bottom-row">
          <div className="bottom-row-container">
            <button
              type="button"
              tabIndex={0}
              className={`${classes.saveChangesButton} button--primary save-changes`}
              onClick={() => {
                saveChanges();
              }}
              onKeyDown={() => {
                saveChanges();
              }}
              disabled={isButtonDisabled()}
            >
              {userProfileCopy.buttons.button1}
            </button>
            <ChangesSaved
              message={userProfileCopy.successMessage}
              stateValToDetermineShowing={updateProfileSuccess}
            />
          </div>
          {/* Remove "remove PII" for now */}
          {/* <div className="grid-item remove-pii">
            <button
              className="link-button remove-pii"
              type="button"
              alt="Remove PII"
              style={{ color: props.userProfile?.tenant.primary }}
            >
              {userProfileCopy.buttons.link2}
            </button>
            <InformationIcon
              color={props.userProfile?.tenant?.primary}
              text="information"
              ariaLabel="click remove pii"
              modalImage=""
            />
          </div> */}
        </div>
      )}
    </>
  );
}

const mapDispatchToProps = { getUserDashboardInfo };

function mapStateToProps(state) {
  const auth = getAuthState(state);
  const onboarding = getOnboardingState(state);
  const userProfile = state.admin.userProfileInfo;

  return { auth, onboarding, userProfile };
}

export default withRouter(
  connect(mapStateToProps, {
    getSchools,
    updateAdvisorDashboardUserProfile,
    getUserDashboardInfo,
    getEligibleUnassignedAdvisorsForUser,
    assignAdvisorsToUser,
    unassignAdvisorFromUser,
    mapDispatchToProps,
  })(UserProfileForm)
);
